<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */

    import { getPointerEventCoords, remToPx } from '@oat-sa-private/ui-core';
    import ResizeAnchor from './ResizeAnchor.svelte';
    import { onDestroy } from 'svelte';

    /**
     * @typedef Geometry
     * @property {Number} x rectangle top-left x-axis
     * @property {Number} y rectangle top-left y-axis
     * @property {Number} width rectangle width
     * @property {Number} height rectangle height
     */

    /**
     * Component is used to draw/display ellipse (oval)
     */

    const bboxOffset = remToPx(0.125);
    const hitboxOffset = remToPx(1);
    const minSize = remToPx(1);

    /**
     * @typedef {Object} Props
     * @property {DrawingGeometry} drawingGeometry - details of draw start
     * @property {Geometry} geometry - individual shape props
     * @property {Boolean} selected - selected state of the shape
     * @property {Function} onFinishDraw
     * @property {Function} onFinishResizing
     */
    /** @type {Props} */
    // prettier-ignore
    let {
        drawingGeometry,
        geometry,
        selected,
        onFinishDraw,
        onFinishResizing
    } = $props();

    let x = $state();
    let y = $state();
    let width = $state();
    let height = $state();
    let startX;
    let startY;
    let initialPointerX;
    let initialPointerY;
    let resizeInitialWidth;
    let resizeInitialHeight;

    // svelte-ignore state_referenced_locally
    if (drawingGeometry) {
        startX = drawingGeometry.drawAreaStartX;
        startY = drawingGeometry.drawAreaStartY;
        initialPointerX = drawingGeometry.initialPointerX;
        initialPointerY = drawingGeometry.initialPointerY;
        width = 0;
        height = 0;

        window.addEventListener('mousemove', draw);
        window.addEventListener('touchmove', draw);
        window.addEventListener('mouseup', finishDraw);
        window.addEventListener('touchend', finishDraw);
    } else if (geometry) {
        x = geometry.x;
        y = geometry.y;
        width = geometry.width;
        height = geometry.height;
    }

    /**
     * Calculates shape props based on mousemove offset related to initial point
     * @param {Event} e
     */
    function draw(e) {
        if (!(e.buttons === 1 || (e.changedTouches && e.changedTouches.length === 1))) {
            finishDraw();
        } else {
            const eventCoords = getPointerEventCoords(e);
            width = eventCoords.x - initialPointerX;
            height = eventCoords.y - initialPointerY;
            if (width > 0) {
                x = startX;
            } else {
                width = Math.abs(width);
                x = startX - width;
            }
            if (height > 0) {
                y = startY;
            } else {
                height = Math.abs(height);
                y = startY - height;
            }
        }
    }

    /**
     * Removes event listeners
     * @fires finishDraw
     */
    function finishDraw() {
        removeWindowEventListeners();
        if (width >= minSize && height >= minSize) {
            onFinishDraw?.({ type: 'rectangle', geometry: { x, y, width, height } });
        } else {
            onFinishDraw?.(false);
        }
    }

    /**
     * Handles start of resizing
     */
    function handleStartResizing() {
        resizeInitialWidth = width;
        resizeInitialHeight = height;
    }

    /**
     * Handles resize event
     * @param {CustomEvent} e
     */
    function handleResize(e) {
        const { dx, dy } = e;
        width = Math.max(resizeInitialWidth + dx, minSize);
        height = Math.max(resizeInitialHeight + dy, minSize);
    }

    /**
     * Handles finishResizing event
     */
    function handleFinishResizing() {
        onFinishResizing?.({ geometry: { x, y, width, height } });
    }

    /**
     * Removes attached event listeners from window
     */
    function removeWindowEventListeners() {
        window.removeEventListener('mousemove', draw);
        window.removeEventListener('touchmove', draw);
        window.removeEventListener('mouseup', finishDraw);
        window.removeEventListener('touchend', finishDraw);
    }

    onDestroy(removeWindowEventListeners);
</script>

<rect class="shape" class:too-small={width < minSize || height < minSize} {x} {y} {width} {height} />
{#if typeof x !== 'undefined' && typeof y !== 'undefined'}
    <rect
        class="hitbox"
        x={x - bboxOffset - hitboxOffset}
        y={y - bboxOffset - hitboxOffset}
        width={width + (bboxOffset + hitboxOffset) * 2}
        height={height + (bboxOffset + hitboxOffset) * 2} />
    <rect
        class="bounding"
        x={x - bboxOffset}
        y={y - bboxOffset}
        width={width + bboxOffset * 2}
        height={height + bboxOffset * 2} />
{/if}
{#if selected}
    <ResizeAnchor
        cx={x + width - bboxOffset}
        cy={y + height - bboxOffset}
        onStartResizing={handleStartResizing}
        onResize={handleResize}
        onFinishResizing={handleFinishResizing} />
{/if}
